if (!customElements.get('faq-search-section')) {
  class FaqSearchSection extends HTMLElement {
    connectedCallback() {
      this._input = this.querySelector('[data-faq-search]');
      this._clearBtn = this.querySelector('[data-faq-clear]');
      this._resultsBar = this.querySelector('[data-faq-results]');
      this._noResults = this.querySelector('[data-faq-no-results]');

      this._cacheItems();
      this._initSearch();
      this._bindEditorEvents();

      // Ensure collapsible triggers inside this component are wired up.
      // theme.collapsibles.init() runs globally at DOMContentLoaded, but calling
      // it here with this element as scope guarantees correct timing with defer.
      this._initCollapsibles();
    }

    disconnectedCallback() {
      if (this._boundSearch) this._input?.removeEventListener('input', this._boundSearch);
      if (this._boundKeydown) this._input?.removeEventListener('keydown', this._boundKeydown);
      if (this._boundClear) this._clearBtn?.removeEventListener('click', this._boundClear);
      if (this._boundSectionLoad) document.removeEventListener('shopify:section:load', this._boundSectionLoad);
      if (this._boundBlockSelect) document.removeEventListener('shopify:block:select', this._boundBlockSelect);
    }

    /* ─── Delegate accordion to theme.collapsibles ─── */
    _initCollapsibles() {
      window.theme?.collapsibles?.init(this);
    }

    /* ─── Cache DOM references and store original text for search ─── */
    _cacheItems() {
      this._groups = [...this.querySelectorAll('.nfb__group')];
      this._items = [...this.querySelectorAll('.nfb__item')];

      this._items.forEach((item) => {
        const qEl = item.querySelector('.nfb__question-text');
        const aEl = item.querySelector('.nfb__answer-text');
        item._origQText = qEl ? qEl.textContent.trim() : '';
        item._origQHtml = qEl ? qEl.innerHTML : '';
        item._origAText = aEl ? aEl.textContent.trim() : '';
        item._origAHtml = aEl ? aEl.innerHTML : '';
      });
    }

    /* ─── Search ─── */
    _initSearch() {
      if (!this._input) return;

      this._boundSearch = () => this._runSearch();
      this._boundClear = () => {
        this._input.value = '';
        this._runSearch();
        this._input.focus();
      };
      this._boundKeydown = (e) => {
        if (e.key === 'Escape' && this._input.value) {
          e.preventDefault();
          this._boundClear();
        }
      };

      this._input.addEventListener('input', this._boundSearch);
      this._input.addEventListener('keydown', this._boundKeydown);
      this._clearBtn?.addEventListener('click', this._boundClear);
    }

    _runSearch() {
      const raw = this._input.value.trim();
      const q = raw.toLowerCase();

      if (this._clearBtn) this._clearBtn.hidden = !raw;

      let totalVisible = 0;

      this._groups.forEach((group) => {
        const items = [...group.querySelectorAll('.nfb__item')];
        let groupVisible = 0;

        items.forEach((item) => {
          const qText = item._origQText.toLowerCase();
          const aText = item._origAText.toLowerCase();
          const matches = !q || qText.includes(q) || aText.includes(q);

          item.hidden = !matches;

          if (matches) {
            groupVisible++;
            totalVisible++;
            this._applyHighlight(item, raw);
          } else {
            this._removeHighlight(item);
          }
        });

        group.hidden = groupVisible === 0;
      });

      if (this._noResults) this._noResults.hidden = totalVisible > 0 || !q;

      this._updateResultsBar(raw, totalVisible);
    }

    _applyHighlight(item, query) {
      const qEl = item.querySelector('.nfb__question-text');
      const aEl = item.querySelector('.nfb__answer-text');

      if (query) {
        if (qEl) qEl.innerHTML = this._highlight(item._origQText, query);
        if (aEl) aEl.innerHTML = this._highlight(item._origAText, query);
      } else {
        if (qEl) qEl.innerHTML = item._origQHtml;
        if (aEl) aEl.innerHTML = item._origAHtml;
      }
    }

    _removeHighlight(item) {
      const qEl = item.querySelector('.nfb__question-text');
      const aEl = item.querySelector('.nfb__answer-text');
      if (qEl) qEl.innerHTML = item._origQHtml;
      if (aEl) aEl.innerHTML = item._origAHtml;
    }

    _highlight(rawText, query) {
      if (!query) return this._escapeHtml(rawText);
      const escapedQ = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedQ})`, 'gi');
      return rawText
        .split(regex)
        .map((part, i) => (i % 2 === 0 ? this._escapeHtml(part) : `<mark>${this._escapeHtml(part)}</mark>`))
        .join('');
    }

    _escapeHtml(str) {
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    _updateResultsBar(query, count) {
      if (!this._resultsBar) return;
      if (!query) {
        this._resultsBar.innerHTML = '';
        return;
      }
      const safeQ = this._escapeHtml(query);
      if (count === 0) {
        this._resultsBar.innerHTML = `No results for &ldquo;<strong>${safeQ}</strong>&rdquo;`;
      } else {
        this._resultsBar.innerHTML = `<strong>${count} result${count !== 1 ? 's' : ''}</strong> for &ldquo;${safeQ}&rdquo;`;
      }
    }

    /* ─── Shopify Theme Editor Events ─── */
    _bindEditorEvents() {
      this._boundSectionLoad = (e) => {
        if (e.detail?.sectionId !== this.dataset.sectionId) return;
        // Re-cache DOM (new blocks may have been added) and re-init collapsibles
        this._cacheItems();
        this._initCollapsibles();
        if (this._input?.value) this._runSearch();
      };

      this._boundBlockSelect = (e) => {
        // When a block is selected in the editor, open it if it's an FAQ item
        const item = e.target.closest('.nfb__item');
        if (!item || !this.contains(item)) return;
        const trigger = item.querySelector('.collapsible-trigger');
        if (trigger && !trigger.classList.contains('is-open')) {
          trigger.click();
        }
      };

      document.addEventListener('shopify:section:load', this._boundSectionLoad);
      document.addEventListener('shopify:block:select', this._boundBlockSelect);
    }
  }

  customElements.define('faq-search-section', FaqSearchSection);
}
